/**
 * Pattern Review — Commercial Learning Layer
 * 
 * Turns accumulated Commercial Briefs into decision-useful BD learning.
 * Each insight includes "what changes" — the commercial behavior implication.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { API } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, Target, AlertTriangle, Lightbulb,
  ChevronRight, BarChart3, GitBranch, Eye, EyeOff, Zap,
  Users, Calendar, ArrowRight, CheckCircle2, XCircle, Clock,
  Signal, Sparkles, Search, Filter, RefreshCw, FileText,
} from "lucide-react";

function SectionLabel({ children }) {
  return <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">{children}</p>;
}

function InsightCard({ insight, type }) {
  const typeConfig = {
    high_performer: { icon: TrendingUp, color: "text-emerald-400", bg: "bg-emerald-900/20", border: "border-emerald-700/30" },
    stall_pattern: { icon: Clock, color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
    poor_performer: { icon: TrendingDown, color: "text-red-400", bg: "bg-red-900/20", border: "border-red-700/30" },
    high_value_channel: { icon: Zap, color: "text-emerald-400", bg: "bg-emerald-900/20", border: "border-emerald-700/30" },
    low_quality_channel: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-900/20", border: "border-red-700/30" },
    under_analyzed: { icon: Eye, color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
    mass_stall: { icon: Clock, color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
    stale_hypotheses: { icon: Calendar, color: "text-orange-400", bg: "bg-orange-900/20", border: "border-orange-700/30" },
    late_stage_loss: { icon: XCircle, color: "text-red-400", bg: "bg-red-900/20", border: "border-red-700/30" },
    recurring_gap: { icon: Search, color: "text-violet-400", bg: "bg-violet-900/20", border: "border-violet-700/30" },
    proof_priority: { icon: Target, color: "text-violet-400", bg: "bg-violet-900/20", border: "border-violet-700/30" },
    primary_loss_reason: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-900/20", border: "border-red-700/30" },
    competitor_pressure: { icon: Users, color: "text-orange-400", bg: "bg-orange-900/20", border: "border-orange-700/30" },
    poor_fit_archetype: { icon: XCircle, color: "text-red-400", bg: "bg-red-900/20", border: "border-red-700/30" },
    missed_warning: { icon: EyeOff, color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
    enrichment_adoption: { icon: Sparkles, color: "text-violet-400", bg: "bg-violet-900/20", border: "border-violet-700/30" },
    enrichment_value: { icon: TrendingUp, color: "text-emerald-400", bg: "bg-emerald-900/20", border: "border-emerald-700/30" },
    instinct_value: { icon: Lightbulb, color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
    high_signal_neglected: { icon: AlertTriangle, color: "text-red-400", bg: "bg-red-900/20", border: "border-red-700/30" },
    high_value_channel_neglected: { icon: EyeOff, color: "text-orange-400", bg: "bg-orange-900/20", border: "border-orange-700/30" },
    intent_drift: { icon: GitBranch, color: "text-amber-400", bg: "bg-amber-900/20", border: "border-amber-700/30" },
    channel_blind_spot: { icon: Eye, color: "text-slate-400", bg: "bg-slate-800/40", border: "border-slate-700/30" },
  };

  const config = typeConfig[insight.type] || { icon: Lightbulb, color: "text-slate-400", bg: "bg-slate-800/40", border: "border-slate-700/30" };
  const Icon = config.icon;

  return (
    <div className={`rounded-lg border p-3 ${config.bg} ${config.border}`} data-testid={`insight-${insight.type}`}>
      <div className="flex items-start gap-3">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${config.color}`} />
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${config.color}`}>{insight.metric}</p>
          <p className="text-xs text-slate-300 mt-1 leading-relaxed">{insight.what_changes}</p>
          {insight.action && (
            <p className="text-[10px] text-slate-500 mt-1 italic">{insight.action}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, subtext, trend, onClick }) {
  return (
    <button 
      onClick={onClick}
      className={`text-left p-4 rounded-lg bg-[#111827] border border-slate-800/60 ${onClick ? "hover:border-slate-700/60 cursor-pointer" : ""} transition-all`}
    >
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">{label}</p>
      <p className="text-2xl font-bold text-slate-100 mt-1">{value}</p>
      {subtext && <p className="text-xs text-slate-500 mt-0.5">{subtext}</p>}
      {trend && (
        <div className={`flex items-center gap-1 mt-1 text-xs ${trend > 0 ? "text-emerald-400" : trend < 0 ? "text-red-400" : "text-slate-500"}`}>
          {trend > 0 ? <TrendingUp className="w-3 h-3" /> : trend < 0 ? <TrendingDown className="w-3 h-3" /> : null}
          <span>{trend > 0 ? "+" : ""}{trend}%</span>
        </div>
      )}
    </button>
  );
}

export default function PatternReview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Data states
  const [wedgeData, setWedgeData] = useState(null);
  const [sourceData, setSourceData] = useState(null);
  const [lifecycleData, setLifecycleData] = useState(null);
  const [proofData, setProofData] = useState(null);
  const [lostData, setLostData] = useState(null);
  const [instinctData, setInstinctData] = useState(null);
  const [missedData, setMissedData] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [wedge, source, lifecycle, proof, lost, instinct, missed] = await Promise.all([
          fetch(`${API}/commercial-briefs/patterns/wedge-performance`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/commercial-briefs/patterns/source-quality`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/commercial-briefs/patterns/lifecycle-stalls`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/commercial-briefs/patterns/proof-gaps`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/commercial-briefs/patterns/lost-signals`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/commercial-briefs/patterns/founder-instinct`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
          fetch(`${API}/commercial-briefs/patterns/missed-signals`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
        ]);
        setWedgeData(wedge);
        setSourceData(source);
        setLifecycleData(lifecycle);
        setProofData(proof);
        setLostData(lost);
        setInstinctData(instinct);
        setMissedData(missed);
      } catch (e) {
        toast.error("Failed to load pattern data");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Aggregate all insights
  const allInsights = [
    ...(wedgeData?.insights || []),
    ...(sourceData?.insights || []),
    ...(lifecycleData?.insights || []),
    ...(proofData?.insights || []),
    ...(lostData?.insights || []),
    ...(instinctData?.insights || []),
    ...(missedData?.insights || []),
  ];

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "wedges", label: "Wedge Performance" },
    { id: "sources", label: "Channel Quality" },
    { id: "lifecycle", label: "Lifecycle" },
    { id: "proof", label: "Proof Gaps" },
    { id: "lost", label: "Lost Analysis" },
    { id: "instinct", label: "Founder Instinct" },
    { id: "missed", label: "Missed Signals" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0e17] text-slate-200" data-testid="pattern-review-page">
      {/* Header */}
      <div className="border-b border-slate-800/60 bg-[#0d1220]">
        <div className="max-w-[1400px] mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5 mb-1">
                <BarChart3 className="w-5 h-5 text-violet-500" />
                <h1 className="text-lg font-bold tracking-tight text-slate-100" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="page-title">
                  Pattern Review
                </h1>
                <Badge variant="outline" className="text-[9px] border-violet-700/50 text-violet-400 ml-1">COMMERCIAL LEARNING</Badge>
              </div>
              <p className="text-xs text-slate-500 max-w-xl">
                Transforms Commercial Briefs into decision-useful BD learning. Each insight shows what changes.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 hover:text-white" onClick={() => navigate("/commercial-briefs")} data-testid="open-commercial-briefs">
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Commercial Briefs
              </Button>
              <Button size="sm" variant="outline" className="border-slate-700 text-slate-400" onClick={() => window.location.reload()}>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-800/40 bg-[#0d1220]/50">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            {tabs.map(tab => (
              <Button key={tab.id} size="sm" variant={activeTab === tab.id ? "default" : "ghost"}
                className={activeTab === tab.id ? "bg-violet-600 text-white" : "text-slate-500 hover:text-slate-300"}
                onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}>
                {tab.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1400px] mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="animate-pulse text-slate-500 text-sm">Loading pattern analysis...</div>
          </div>
        ) : activeTab === "overview" ? (
          <OverviewTab 
            wedgeData={wedgeData}
            sourceData={sourceData}
            lifecycleData={lifecycleData}
            lostData={lostData}
            instinctData={instinctData}
            allInsights={allInsights}
            navigate={navigate}
          />
        ) : activeTab === "wedges" ? (
          <WedgeTab data={wedgeData} navigate={navigate} />
        ) : activeTab === "sources" ? (
          <SourceTab data={sourceData} navigate={navigate} />
        ) : activeTab === "lifecycle" ? (
          <LifecycleTab data={lifecycleData} navigate={navigate} />
        ) : activeTab === "proof" ? (
          <ProofTab data={proofData} />
        ) : activeTab === "lost" ? (
          <LostTab data={lostData} />
        ) : activeTab === "instinct" ? (
          <InstinctTab data={instinctData} />
        ) : activeTab === "missed" ? (
          <MissedTab data={missedData} navigate={navigate} />
        ) : null}
      </div>
    </div>
  );
}


// ─── Overview Tab ─────────────────────────────────────────────

function OverviewTab({ wedgeData, sourceData, lifecycleData, lostData, instinctData, allInsights, navigate }) {
  const totalBriefs = lifecycleData?.total_briefs || 0;
  const winLossRatio = lostData?.win_loss_ratio || "N/A";
  const enrichedCount = instinctData?.enriched_count || 0;
  const quickCreateTotal = instinctData?.quick_create_total || 0;

  // Prioritize high-impact insights
  const priorityInsights = allInsights.filter(i => 
    ["high_signal_neglected", "high_value_channel_neglected", "late_stage_loss", "poor_performer", "recurring_gap"].includes(i.type)
  );
  const otherInsights = allInsights.filter(i => !priorityInsights.includes(i));

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Key Metrics */}
      <div className="grid grid-cols-5 gap-4">
        <StatCard label="Total Briefs" value={totalBriefs} />
        <StatCard label="Win/Loss Ratio" value={winLossRatio} subtext={`${lostData?.total_won || 0} won, ${lostData?.total_lost || 0} lost`} />
        <StatCard label="Hypothesis Stuck" value={lifecycleData?.status_distribution?.hypothesis || 0} subtext="at first stage" />
        <StatCard label="Quick Creates" value={quickCreateTotal} subtext={`${enrichedCount} enriched`} />
        <StatCard label="Stale (30d+)" value={lifecycleData?.stale_hypotheses || 0} subtext="need review" />
      </div>

      {/* Priority Insights */}
      {priorityInsights.length > 0 && (
        <div>
          <SectionLabel>Priority Actions</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {priorityInsights.slice(0, 4).map((insight, i) => (
              <InsightCard key={`priority-${i}`} insight={insight} />
            ))}
          </div>
        </div>
      )}

      {/* All Insights */}
      {allInsights.length > 0 ? (
        <div>
          <SectionLabel>All Insights ({allInsights.length})</SectionLabel>
          <div className="grid grid-cols-3 gap-3">
            {allInsights.map((insight, i) => (
              <InsightCard key={`all-${i}`} insight={insight} />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <BarChart3 className="w-10 h-10 text-slate-700 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Not enough data for pattern analysis yet</p>
          <p className="text-xs text-slate-600 mt-1">Create more Commercial Briefs to see insights</p>
          <Button size="sm" className="mt-4 bg-violet-600 text-white" onClick={() => navigate("/commercial-briefs")}>
            View Briefs
          </Button>
        </div>
      )}
    </div>
  );
}


// ─── Wedge Tab ────────────────────────────────────────────────

function WedgeTab({ data, navigate }) {
  if (!data) return <EmptyState message="No wedge data available" />;

  return (
    <div className="space-y-6" data-testid="wedge-tab">
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Wedges Used" value={data.wedge_stats?.length || 0} />
        <StatCard label="Total Analyzed" value={data.total_analyzed || 0} />
      </div>

      {data.insights?.length > 0 && (
        <div>
          <SectionLabel>Wedge Insights</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {data.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
          </div>
        </div>
      )}

      <div>
        <SectionLabel>Wedge Performance</SectionLabel>
        <div className="space-y-2">
          {data.wedge_stats?.map((w, i) => (
            <div key={i} className="p-3 rounded-lg bg-[#111827] border border-slate-800/60 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-200">{w._id}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  <span>{w.total} briefs</span>
                  <span className="text-emerald-400">{w.won} won</span>
                  <span className="text-red-400">{w.lost} lost</span>
                  <span className="text-amber-400">{w.hypothesis_stuck} stuck</span>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-lg font-bold ${w.win_rate >= 40 ? "text-emerald-400" : w.win_rate >= 20 ? "text-amber-400" : "text-slate-400"}`}>
                  {w.win_rate}%
                </p>
                <p className="text-[10px] text-slate-500">win rate</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Source Tab ───────────────────────────────────────────────

function SourceTab({ data, navigate }) {
  if (!data) return <EmptyState message="No source data available" />;

  return (
    <div className="space-y-6" data-testid="source-tab">
      {data.insights?.length > 0 && (
        <div>
          <SectionLabel>Channel Insights</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {data.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
          </div>
        </div>
      )}

      <div>
        <SectionLabel>Channel Performance</SectionLabel>
        <div className="space-y-2">
          {data.source_stats?.map((s, i) => (
            <div key={i} className="p-3 rounded-lg bg-[#111827] border border-slate-800/60 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-200">{s.label}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                  <span>{s.total} briefs</span>
                  <span className="text-emerald-400">{s.won} won</span>
                  <span className="text-red-400">{s.lost} lost</span>
                  <span>{s.high_signal_rate}% high-signal</span>
                </div>
              </div>
              <div className="text-right">
                <p className={`text-lg font-bold ${s.win_rate >= 40 ? "text-emerald-400" : s.win_rate >= 20 ? "text-amber-400" : "text-slate-400"}`}>
                  {s.win_rate}%
                </p>
                <p className="text-[10px] text-slate-500">win rate</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Lifecycle Tab ────────────────────────────────────────────

function LifecycleTab({ data, navigate }) {
  if (!data) return <EmptyState message="No lifecycle data available" />;

  const statusLabels = {
    hypothesis: "Hypothesis",
    validated_in_conversation: "Validated",
    converted_to_proposal: "Proposal",
    won: "Won",
    lost: "Lost",
    expansion_opportunity: "Expansion",
    content_theme_extracted: "Content Extracted",
    archived: "Archived",
  };

  return (
    <div className="space-y-6" data-testid="lifecycle-tab">
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Briefs" value={data.total_briefs || 0} />
        <StatCard label="Stale (30d+)" value={data.stale_hypotheses || 0} />
        <StatCard label="Lost After Proposal" value={data.proposal_to_lost || 0} />
        <StatCard label="Avg Transitions" value={data.avg_transitions || 0} subtext="per brief" />
      </div>

      {data.insights?.length > 0 && (
        <div>
          <SectionLabel>Lifecycle Insights</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {data.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
          </div>
        </div>
      )}

      <div>
        <SectionLabel>Status Distribution</SectionLabel>
        <div className="grid grid-cols-4 gap-3">
          {Object.entries(data.status_distribution || {}).map(([status, count]) => (
            <div key={status} className="p-3 rounded-lg bg-[#111827] border border-slate-800/60">
              <p className="text-xs text-slate-500">{statusLabels[status] || status}</p>
              <p className="text-xl font-bold text-slate-100 mt-1">{count}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ─── Proof Tab ────────────────────────────────────────────────

function ProofTab({ data }) {
  if (!data) return <EmptyState message="No proof gap data available" />;

  return (
    <div className="space-y-6" data-testid="proof-tab">
      {data.insights?.length > 0 && (
        <div>
          <SectionLabel>Proof Insights</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {data.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div>
          <SectionLabel>Most Needed Proof ({data.proof_gaps?.length || 0})</SectionLabel>
          <div className="space-y-2">
            {data.proof_gaps?.map((gap, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#111827] border border-slate-800/60 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-200">{gap._id}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {gap.sectors?.slice(0, 2).join(", ")}
                  </p>
                </div>
                <Badge className="text-xs bg-violet-900/30 text-violet-400 border-violet-700/30 border">
                  {gap.count}x needed
                </Badge>
              </div>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>Available Proof ({data.proof_available?.length || 0})</SectionLabel>
          <div className="space-y-2">
            {data.proof_available?.map((proof, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#111827] border border-slate-800/60 flex items-center justify-between">
                <p className="text-sm text-slate-200">{proof._id}</p>
                <Badge className="text-xs bg-emerald-900/30 text-emerald-400 border-emerald-700/30 border">
                  {proof.count}x used
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Lost Tab ─────────────────────────────────────────────────

function LostTab({ data }) {
  if (!data) return <EmptyState message="No lost analysis data available" />;

  const qualityLabels = {
    "strong-fit": "Strong Fit",
    "partial-fit": "Partial Fit",
    "poor-fit": "Poor Fit",
    "no-budget": "No Budget",
    "timing-issue": "Timing Issue",
    "relationship-blocked": "Relationship Blocked",
    "wrong-buyer": "Wrong Buyer",
    "lost-to-competitor": "Lost to Competitor",
  };

  return (
    <div className="space-y-6" data-testid="lost-tab">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Total Lost" value={data.total_lost || 0} />
        <StatCard label="Total Won" value={data.total_won || 0} />
        <StatCard label="Win/Loss Ratio" value={data.win_loss_ratio || "N/A"} />
      </div>

      {data.insights?.length > 0 && (
        <div>
          <SectionLabel>Loss Pattern Insights</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {data.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-6">
        <div>
          <SectionLabel>Loss Reasons</SectionLabel>
          <div className="space-y-2">
            {data.loss_reasons?.map((reason, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#111827] border border-slate-800/60 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-200">{qualityLabels[reason._id] || reason._id}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {reason.sectors?.slice(0, 2).join(", ")}
                  </p>
                </div>
                <Badge className="text-xs bg-red-900/30 text-red-400 border-red-700/30 border">
                  {reason.count} losses
                </Badge>
              </div>
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>Contraindications on Lost Deals</SectionLabel>
          <div className="space-y-2">
            {data.contraindications?.map((contra, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#111827] border border-slate-800/60 flex items-center justify-between">
                <p className="text-sm text-slate-200">{contra._id}</p>
                <Badge className="text-xs bg-amber-900/30 text-amber-400 border-amber-700/30 border">
                  {contra.count}x
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Instinct Tab ─────────────────────────────────────────────

function InstinctTab({ data }) {
  if (!data) return <EmptyState message="No founder instinct data available" />;

  return (
    <div className="space-y-6" data-testid="instinct-tab">
      <div className="grid grid-cols-5 gap-4">
        <StatCard label="Quick Creates" value={data.quick_create_total || 0} />
        <StatCard label="Enriched" value={data.enriched_count || 0} />
        <StatCard label="Unenriched" value={data.unenriched_count || 0} />
        <StatCard label="Direct Analysis" value={data.direct_analysis_count || 0} />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-emerald-950/20 border-emerald-800/40">
          <CardContent className="pt-4">
            <p className="text-xs text-emerald-400 font-semibold">Enriched Win Rate</p>
            <p className="text-3xl font-bold text-emerald-300 mt-1">{data.enriched_win_rate || 0}%</p>
            <p className="text-xs text-emerald-500/70 mt-1">Quick Create → Entry Intelligence</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-950/20 border-amber-800/40">
          <CardContent className="pt-4">
            <p className="text-xs text-amber-400 font-semibold">Unenriched Win Rate</p>
            <p className="text-3xl font-bold text-amber-300 mt-1">{data.unenriched_win_rate || 0}%</p>
            <p className="text-xs text-amber-500/70 mt-1">Quick Create only</p>
          </CardContent>
        </Card>
        <Card className="bg-violet-950/20 border-violet-800/40">
          <CardContent className="pt-4">
            <p className="text-xs text-violet-400 font-semibold">Direct Analysis Win Rate</p>
            <p className="text-3xl font-bold text-violet-300 mt-1">{data.direct_win_rate || 0}%</p>
            <p className="text-xs text-violet-500/70 mt-1">Entry Intelligence first</p>
          </CardContent>
        </Card>
      </div>

      {data.insights?.length > 0 && (
        <div>
          <SectionLabel>Instinct vs Analysis Insights</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {data.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Missed Signals Tab ───────────────────────────────────────

function MissedTab({ data, navigate }) {
  if (!data) return <EmptyState message="No missed signal data available" />;

  return (
    <div className="space-y-6" data-testid="missed-tab">
      {data.insights?.length > 0 && (
        <div>
          <SectionLabel>BD Blind Spot Insights</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            {data.insights.map((insight, i) => <InsightCard key={i} insight={insight} />)}
          </div>
        </div>
      )}

      {data.high_signal_stale?.length > 0 && (
        <div>
          <SectionLabel>High-Signal Captures Never Followed Up ({data.high_signal_stale.length})</SectionLabel>
          <div className="space-y-2">
            {data.high_signal_stale.map((brief, i) => (
              <button key={i} onClick={() => navigate(`/commercial-briefs/${brief.brief_id}`)}
                className="w-full text-left p-3 rounded-lg bg-[#111827] border border-slate-800/60 hover:border-slate-700/60 transition-all flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-200">{brief.account_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {brief.opportunity_source?.replace(/_/g, " ")} • {brief.created_at?.slice(0, 10)}
                  </p>
                </div>
                <Badge className="text-[9px] bg-red-900/20 text-red-400 border-red-700/30 border">High Signal</Badge>
              </button>
            ))}
          </div>
        </div>
      )}

      {data.high_value_stale?.length > 0 && (
        <div>
          <SectionLabel>High-Value Channels Gone Cold ({data.high_value_stale.length})</SectionLabel>
          <div className="space-y-2">
            {data.high_value_stale.map((brief, i) => (
              <button key={i} onClick={() => navigate(`/commercial-briefs/${brief.brief_id}`)}
                className="w-full text-left p-3 rounded-lg bg-[#111827] border border-slate-800/60 hover:border-slate-700/60 transition-all flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-200">{brief.account_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {brief.opportunity_source?.replace(/_/g, " ")} • {brief.known_buyer_contact || "No contact"}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600" />
              </button>
            ))}
          </div>
        </div>
      )}

      {data.intent_not_acted?.length > 0 && (
        <div>
          <SectionLabel>Intent Stated But Not Acted ({data.intent_not_acted.length})</SectionLabel>
          <div className="space-y-2">
            {data.intent_not_acted.map((brief, i) => (
              <button key={i} onClick={() => navigate(`/commercial-briefs/${brief.brief_id}`)}
                className="w-full text-left p-3 rounded-lg bg-[#111827] border border-slate-800/60 hover:border-slate-700/60 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-200">{brief.account_name}</p>
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </div>
                <p className="text-xs text-amber-400/80 mt-1 italic">"{brief.next_conversation_intent?.slice(0, 60)}..."</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {data.unused_sources?.length > 0 && (
        <div>
          <SectionLabel>Unused Channels (Potential Blind Spots)</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {data.unused_sources.map((source, i) => (
              <Badge key={i} className="text-xs bg-slate-800/50 text-slate-400 border-slate-700/30 border">
                {source.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Empty State ──────────────────────────────────────────────

function EmptyState({ message }) {
  return (
    <div className="text-center py-20">
      <BarChart3 className="w-10 h-10 text-slate-700 mx-auto mb-3" />
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  );
}
